"use client";

import { useEffect, useMemo, useState } from "react";

import Image from "next/image";
import Link from "next/link";

import type { SanityImageSource } from "@sanity/image-url";
import cn from "classnames";

import { AccordionCompact } from "@/components/accordionCompact/accordionCompact.component";
import { Button } from "@/components/button/button.component";
import { PlusIcon } from "@/components/icons/plus.icon";
import { ProductDetailsDrawerAdtAppCarouselSection } from "@/components/productDetailDrawer/productDetailsDrawerAdtAppCarouselSection.component";
import { RichText } from "@/components/richText/richText.component";
import { Text } from "@/components/text/text.component";

import { SHOP_PAGE } from "@/constants/page.constants";
import type {
  GetAccessoryDetailPageQueryResult,
  GetProductQueryResult,
} from "@/sanity.types";
import { urlFor } from "@/sanity/utils";

function optionalImageUrl(
  image: SanityImageSource | null | undefined,
  width: number,
) {
  if (!image) return null;
  try {
    return urlFor(image).width(width).url();
  } catch {
    return null;
  }
}

export const ProductDetailsDrawerContent = ({
  product,
  productDetailDrawerAdtAppCarousel,
}: ProductDetailsDrawerContentProps) => {
  const [mainIndex, setMainIndex] = useState(0);
  const [colorIndex, setColorIndex] = useState(0);

  useEffect(() => {
    setMainIndex(0);
  }, [product._id]);

  const imageRows = product.images ?? [];
  const resolvedImages = useMemo(
    () =>
      imageRows
        .map((row) => ({
          key: row._key,
          alt: row.alt ?? "",
          thumb: optionalImageUrl(row.image, 160),
          hero: optionalImageUrl(row.image, 1200),
        }))
        .filter((r) => r.hero),
    [imageRows],
  );

  const main = resolvedImages[mainIndex] ?? resolvedImages[0];
  const colorOption = product.options?.find((o) => o.variant === "color");
  const colorValues = colorOption?.optionValues ?? [];
  const selectedColor = colorValues[colorIndex];
  const shopHref = product.slug ?? SHOP_PAGE;

  const renderAccordionSections = () => {
    const sections = product.accordionSections;
    if (!sections?.length) return null;
    return (
      <>
        {sections.map((section) => (
          <AccordionCompact
            accordionName="equipment-drawer-details"
            key={section._key}
            title={section.title ?? ""}>
            {section.description ? (
              <div className="text-greyscale-body [&_li]:text-greyscale-body [&_p]:text-greyscale-body [&_ul]:my-2">
                <RichText>{section.description}</RichText>
              </div>
            ) : (
              <Text
                variant="body2"
                component="p"
                className="text-greyscale-strength/80">
                No details for this section yet.
              </Text>
            )}
          </AccordionCompact>
        ))}
      </>
    );
  };

  const renderImages = () => {
    if (resolvedImages.length === 0) {
      return null;
    }

    return (
      <div className="flex flex-col gap-3">
        {main?.hero ? (
          <div className="bg-greyscale-background/60 relative aspect-[4/3] w-full overflow-hidden">
            <Image
              src={main.hero}
              alt={main.alt}
              fill
              className="object-contain p-4"
              sizes="(max-width: 900px) 100vw, 900px"
              priority
            />
          </div>
        ) : null}

        {resolvedImages.length > 1 ? (
          <div className="flex gap-2 overflow-x-auto px-4 pb-1">
            {resolvedImages.map((img, index) => (
              <button
                key={img.key}
                type="button"
                onClick={() => setMainIndex(index)}
                className={cn(
                  "border-greyscale-strength/25 relative h-16 w-16 shrink-0 overflow-hidden border-2 bg-white p-1 transition-colors",
                  index === mainIndex &&
                    "border-secondary ring-secondary/30 ring-2",
                )}
                aria-label={`Show image ${index + 1}`}>
                {img.thumb ? (
                  <Image
                    src={img.thumb}
                    alt=""
                    width={64}
                    height={64}
                    className="h-full w-full object-contain"
                  />
                ) : null}
              </button>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderColorOptions = () => {
    return (
      <>
        {colorValues.length > 0 ? (
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap items-center gap-2">
              {colorValues.map((value, index) => {
                const src = optionalImageUrl(value.image?.[0], 80);
                return (
                  <button
                    key={value.id ?? `color-${index}`}
                    type="button"
                    onClick={() => setColorIndex(index)}
                    aria-label={value.title ?? `Color ${index + 1}`}
                    aria-pressed={index === colorIndex}
                    className={cn(
                      "h-10 w-10 shrink-0 overflow-hidden rounded-full border-2 transition-shadow",
                      index === colorIndex
                        ? "border-secondary ring-secondary/40 ring-2"
                        : "border-greyscale-strength/30",
                    )}>
                    {src ? (
                      <Image
                        src={src}
                        alt=""
                        width={40}
                        height={40}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span className="bg-greyscale-background block h-full w-full" />
                    )}
                  </button>
                );
              })}
            </div>
            {selectedColor?.title ? (
              <Text variant="body4" className="text-greyscale-body">
                Color: {selectedColor.title}
              </Text>
            ) : null}
          </div>
        ) : (
          <span className="hidden sm:block" />
        )}
      </>
    );
  };

  return (
    <div className="flex h-full max-h-full min-h-0 flex-col overflow-hidden">
      <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="flex flex-col gap-6 pt-4 pb-6">
          {renderImages()}

          <div className="flex flex-col gap-6 px-6">
            {product.shortDescription ? (
              <Text
                variant="body1"
                component="p"
                className="text-greyscale-body">
                {product.shortDescription}
              </Text>
            ) : null}
            <div
              className="border-greyscale-strength/25 flex flex-col border-t"
              aria-label="Product details">
              {renderAccordionSections()}
            </div>
          </div>

          {productDetailDrawerAdtAppCarousel?.items?.length ? (
            <ProductDetailsDrawerAdtAppCarouselSection
              key={product._id}
              data={productDetailDrawerAdtAppCarousel}
            />
          ) : null}
        </div>
      </div>

      <footer className="flex shrink-0 justify-end px-10 py-6 shadow-[0_-12px_24px_0_rgba(0,0,0,0.14)]">
        {renderColorOptions()}

        <Link href={shopHref}>
          <Button secondary className="hidden justify-end px-12 py-3 md:flex">
            Add to system <PlusIcon width={24} height={24} />
          </Button>

          <Button secondary className="flex justify-end px-4 py-3 md:hidden">
            Add <PlusIcon width={24} height={24} />
          </Button>
        </Link>
      </footer>
    </div>
  );
};

export type ProductDetailsDrawerContentProps = {
  product: NonNullable<GetProductQueryResult>;
  productDetailDrawerAdtAppCarousel?:
    | NonNullable<GetAccessoryDetailPageQueryResult>["adtPlusApp"]
    | null;
};
